/**
 * File-system routing (convention over configuration).
 *
 * Uses Vite's import.meta.glob at build time to pull every shell under
 * src/app/routes/. File path to URL segment mapping:
 *   - __layout.tsx          : no path; wraps sibling routes with MobileShell
 *   - index.tsx             : parent layout's default child route
 *   - [name].tsx            : dynamic segment (becomes :name)
 *   - foo.tsx               : /foo
 *   - a/b.tsx               : /a/b
 *
 * Vite-plugin-pages has compatibility issues with React 18 + RR v6 on
 * route loaders, so we hand-roll a minimal glob scanner. This keeps the
 * route tree inspectable: read src/app/routes/ to see the full table.
 */

import React, { Suspense } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { LoadingScreen } from '../../shared/ui/LoadingScreen';

type PageModule = { default: React.ComponentType<unknown> };
type LayoutModule = { default: React.ComponentType };

/**
 * Files in src/app/routes/ that are NOT routed through the eager glob.
 *
 *  - __layout.tsx : the layout wrapper, pulled in separately above.
 *  - routeTree.tsx : this file itself.
 */
const SKIP_FILES = new Set(['__layout.tsx', 'routeTree.tsx']);

/** Map a glob key like "./album/[id].tsx" to URL segments. */
function fileToSegments(file: string): string[] {
  const trimmed = file.replace(/^\.\//, '').replace(/\.tsx$/, '');
  const segs = trimmed.split('/').filter(Boolean);
  return segs.map((seg) => {
    if (seg === 'index') return '';
    if (seg.startsWith('[') && seg.endsWith(']')) return `:${seg.slice(1, -1)}`;
    return seg;
  });
}

export const routeTree: RouteObject[] = (() => {
  // ── Glob 改成 lazy（默认 eager=false），让 Vite 给每个路由文件生成独立 chunk ──
  // 这样 ApiDocsPage 的 swagger-ui-react / admin-page 的重组件只会在用户
  // 真正访问那条路由时才下载，主 bundle 不再被它们拖到 1.3MB+。
  // login.tsx 仍走单独的 loginRoute，下面 set 里排除它。
  const layoutModules = import.meta.glob<LayoutModule>('./__layout.tsx', { eager: true });
  const pageModuleLoaders = import.meta.glob<PageModule>(
    ['./!(__layout|routeTree|login).tsx', './*/!(__layout|routeTree).tsx'],
  );

  const layoutModule = layoutModules['./__layout.tsx'];
  if (!layoutModule) {
    throw new Error('[routeTree] __layout.tsx not found under src/app/routes/');
  }

  const routeFallback = <LoadingScreen label="页面加载中" />;

  const children: RouteObject[] = [];

  for (const [file, loader] of Object.entries(pageModuleLoaders)) {
    const basename = file.replace(/^\.\//, '');
    if (SKIP_FILES.has(basename)) continue;

    const segs = fileToSegments(file);

    const buildLazyRoute = (): RouteObject['lazy'] => async () => {
      const mod = await loader();
      const Comp = (mod && mod.default) || (() => null);
      return {
        Component: () => (
          <Suspense fallback={routeFallback}>
            <Comp />
          </Suspense>
        ),
      };
    };

    // 顶层 index.tsx → 父 layout 的默认子路由
    if (segs.length === 1 && segs[0] === '') {
      children.push({ index: true, lazy: buildLazyRoute() });
      continue;
    }

    // 子目录的 index.tsx：把 element 挂到父级 path 上作为 index 子路由。
    // 当父路由已存在（如 manage.tsx 布局 + manage/index.tsx），直接把父路由
    // 升级为 layout：清空 lazy/element 子字段，附加 indexRoute 作为 children。
    if (segs.length >= 2 && segs[segs.length - 1] === '') {
      const parentSegs = segs.slice(0, -1);
      const parentPath = parentSegs.join('/');
      const indexRoute: RouteObject = { index: true, lazy: buildLazyRoute() };
      const existing = children.find((c) => 'path' in c && c.path === parentPath);
      if (existing) {
        if (!('children' in existing) || !Array.isArray(existing.children)) {
          // 父路由之前是 leaf（lazy / element），转为 layout。
          existing.children = [];
        }
        existing.children.push(indexRoute);
      } else {
        children.push({
          path: parentPath,
          children: [indexRoute],
        });
      }
      continue;
    }

    // 子目录里的常规 .tsx（如 manage/inbox.tsx）：若父路由已升级为
    // layout（带 children 数组），把这条挂到父路由里。否则作为扁平兄弟路由
    // 处理（与 account/name.tsx 等老路径保持兼容）。
    if (segs.length >= 2) {
      const parentPath = segs[0];
      const existingParent = children.find((c) => 'path' in c && c.path === parentPath);
      if (existingParent && 'children' in existingParent && Array.isArray(existingParent.children)) {
        existingParent.children.push({
          path: segs.slice(1).join('/'),
          lazy: buildLazyRoute(),
        });
        continue;
      }
    }

    children.push({ path: segs.join('/'), lazy: buildLazyRoute() });
  }

  // 兜底：未知路径跳回 /home
  children.push({ path: '*', element: <Navigate to="/home" replace /> });

  const LayoutComponent = layoutModule.default;
  return [
    {
      path: '/',
      element: React.createElement(LayoutComponent),
      children,
    },
  ];
})();

/** /login 在 layout 之外（全屏未登录态），单独导出。 */
export const loginRoute: RouteObject = {
  path: '/login',
  lazy: async () => {
    const mod = await import('./login');
    return { Component: mod.default };
  },
};
